// src/pages/SupportPage.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../app/store";
import Footer from "./Footer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  createSupportTicket,
  resetCreateTicketStatus,
} from "../features/contactSlice";

const primaryColor = "#a1e0e5";

const SupportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #000c0d;
  color: ${primaryColor};
  padding: 20px;
  min-height: 100vh;
`;

const SupportHeader = styled.h1`
  font-size: 30px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 30px;
  color: ${primaryColor};
`;

const SupportForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  background-color: #1a1a1a;
  padding: 15px;
  border-radius: 10px;
  border: 2px solid ${primaryColor};
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 6px;
  color: ${primaryColor};
`;

const Input = styled.input<{ $invalid?: boolean }>`
  padding: 8px;
  margin-bottom: 6px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#ff4d4f" : primaryColor)};
  border-radius: 5px;
  background-color: #0e0e0e;
  color: ${primaryColor};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ $invalid }) => ($invalid ? "#ff7875" : "#ffffff")};
  }
`;

const TextArea = styled.textarea<{ $invalid?: boolean }>`
  padding: 8px;
  margin-bottom: 6px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#ff4d4f" : primaryColor)};
  border-radius: 5px;
  background-color: #0e0e0e;
  color: ${primaryColor};
  font-size: 14px;
  resize: none;
  height: 120px;

  &:focus {
    outline: none;
    border-color: ${({ $invalid }) => ($invalid ? "#ff7875" : "#ffffff")};
  }
`;

const FieldError = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-bottom: 10px;
  min-height: 16px;
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  padding: 10px;
  background-color: ${primaryColor};
  color: #000;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background-color: #82c5cc;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.p`
  font-size: 14px;
  color: ${primaryColor};
  margin-top: 15px;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #ff0000;
  margin-top: 15px;
`;

const Spacer = styled.div`
  height: 100px;
`;

type FormErrors = Partial<Record<"name" | "email" | "subject" | "message", string>>;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SupportPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // contact slice state
  const status = useSelector((s: RootState) => s.contact.createTicketStatus);
  const error = useSelector((s: RootState) => s.contact.createTicketError);
  const loading = useSelector((s: RootState) => s.contact.isCreateTicketInProgress);
  const lastResponse = useSelector((s: RootState) => s.contact.lastTicketResponse);

  // userId from state.user.user?.id (omit if missing)
  
  const userId = useSelector((s: RootState) => {
    const id = s.authSlice.user?.id;
    return typeof id === "string" && id.trim() ? id : undefined;
  });

  useEffect(() => {
    return () => {
      dispatch(resetCreateTicketStatus());
    };
  }, [dispatch]);

  // Toast on success/failure
  useEffect(() => {
    if (status === "succeeded" && lastResponse?.detail) {
      toast.success(lastResponse.detail);
      // clear form
      setFormData({ name: "", email: "", subject: "", message: "" });
      setErrors({});
    }
    if (status === "failed" && error) {
      toast.error(error);
    }
  }, [status, lastResponse, error]);

  const validate = () => {
    const e: FormErrors = {};
    if (!formData.name.trim()) e.name = "Name is required.";
    if (!formData.email.trim()) e.email = "Email is required.";
    else if (!emailRegex.test(formData.email.trim())) e.email = "Enter a valid email address.";
    if (!formData.subject.trim()) e.subject = "Subject is required.";
    if (!formData.message.trim()) e.message = "Message is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = evt.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // live-validate just this field
    setErrors((prev) => {
      const next = { ...prev };
      if (name === "email") {
        if (!value.trim()) next.email = "Email is required.";
        else if (!emailRegex.test(value.trim())) next.email = "Enter a valid email address.";
        else delete next.email;
      } else {
        if (!value.trim()) (next as any)[name] = "This field is required.";
        else delete (next as any)[name];
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted errors.");
      return;
    }

    dispatch(
      createSupportTicket({
        payload: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          platform: "boxchaos",
        },
        userId, // only sent if defined
      })
    );
  };

  const successText = lastResponse?.detail ?? null;

  return (
    <>
      <SupportWrapper>
        <ToastContainer position="top-center" autoClose={2500} hideProgressBar />
        <SupportHeader>Contact Support</SupportHeader>

        <SupportForm onSubmit={handleSubmit} noValidate>
          <Label htmlFor="name">Name</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            aria-invalid={!!errors.name}
            $invalid={!!errors.name}
            autoComplete="name"
          />
          <FieldError>{errors.name || ""}</FieldError>

          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            aria-invalid={!!errors.email}
            $invalid={!!errors.email}
            autoComplete="email"
          />
          <FieldError>{errors.email || ""}</FieldError>

          <Label htmlFor="subject">Subject</Label>
          <Input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            aria-invalid={!!errors.subject}
            $invalid={!!errors.subject}
          />
          <FieldError>{errors.subject || ""}</FieldError>

          <Label htmlFor="message">Message</Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            aria-invalid={!!errors.message}
            $invalid={!!errors.message}
          />
          <FieldError>{errors.message || ""}</FieldError>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? "Sending..." : "Send Message"}
          </SubmitButton>
        </SupportForm>

        {/* Keep these if you also want inline page feedback */}
        {status === "succeeded" && successText && (
          <SuccessMessage>{successText}</SuccessMessage>
        )}
        {status === "failed" && error && <ErrorMessage>{error}</ErrorMessage>}

        <Footer />
      </SupportWrapper>
      <Spacer />
    </>
  );
};

export default SupportPage;
